import produce from 'immer';
import type { IUserMediaAsset, IUserMediaPagination } from '../../types/global/userMedia.types';
import { types, type IListUserMediaAssetsPayload, type IListUserMediaAssetsSuccessPayload } from './actions';

const LIST_CACHE_TTL_MS = 60_000;

export interface IUserMediaState {
    assets: IUserMediaAsset[];
    pagination: IUserMediaPagination | null;
    loading: boolean;
    loadingMore: boolean;
    error: unknown | null;
    lastQuery: string;
    /** True after a page-1 fetch settles (success or fail) — blocks empty-list refetch loops */
    listSettled: boolean;
    /** After an append returns no new items / fail, stop hasMore auto-fill */
    exhausted: boolean;
    /** cacheKey → fetchedAt for skip-refetch */
    listCacheAt: Record<string, number>;
    mutatingAssetId: string | null;
}

const initialState: IUserMediaState = {
    assets: [],
    pagination: null,
    loading: false,
    loadingMore: false,
    error: null,
    lastQuery: '',
    listSettled: false,
    exhausted: false,
    listCacheAt: {},
    mutatingAssetId: null,
};

export const reducer = (state = initialState, action: { type: string; payload?: any }) => {
    const { type, payload } = action;
    switch (type) {
        case types.LIST_USER_MEDIA_ASSETS: {
            const p = (payload ?? {}) as IListUserMediaAssetsPayload;
            return produce(state, draft => {
                draft.error = null;
                if (p.append) {
                    draft.loadingMore = true;
                } else {
                    draft.loading = true;
                    draft.exhausted = false;
                    const nextQ = (p.q ?? '').trim();
                    if (nextQ !== draft.lastQuery || p.force) {
                        draft.listSettled = false;
                    }
                    if (nextQ !== draft.lastQuery) {
                        draft.assets = [];
                        draft.pagination = null;
                    }
                }
            });
        }
        case types.LIST_USER_MEDIA_ASSETS_SUCCESS: {
            const p = payload as IListUserMediaAssetsSuccessPayload;
            return produce(state, draft => {
                draft.loading = false;
                draft.loadingMore = false;
                draft.error = null;
                draft.pagination = p.pagination;
                draft.lastQuery = p.q;
                draft.listSettled = true;
                draft.listCacheAt[p.cacheKey] = Date.now();
                if (p.append) {
                    const before = draft.assets.length;
                    const seen = new Set(draft.assets.map(a => a.id || a.url));
                    for (const asset of p.assets) {
                        const key = asset.id || asset.url;
                        if (!seen.has(key)) {
                            draft.assets.push(asset);
                            seen.add(key);
                        }
                    }
                    if (draft.assets.length === before || p.assets.length === 0) {
                        draft.exhausted = true;
                    }
                } else {
                    draft.assets = p.assets;
                    // Empty page-1 is settled, not "more to load"
                    draft.exhausted = p.assets.length === 0;
                }
            });
        }
        case types.LIST_USER_MEDIA_ASSETS_FAIL:
            return produce(state, draft => {
                draft.loading = false;
                draft.loadingMore = false;
                draft.error = payload;
                draft.listSettled = true;
                draft.exhausted = true;
            });
        case types.PREPEND_USER_MEDIA_ASSET:
            return produce(state, draft => {
                const asset = payload as IUserMediaAsset;
                const key = asset.id || asset.url;
                draft.assets = [asset, ...draft.assets.filter(a => (a.id || a.url) !== key)];
                draft.exhausted = false;
                draft.listSettled = true;
                if (draft.pagination) {
                    draft.pagination.total += 1;
                }
            });
        case types.UPDATE_USER_MEDIA_ASSET:
            return produce(state, draft => {
                draft.mutatingAssetId = (payload as { id: string }).id;
                draft.error = null;
            });
        case types.UPDATE_USER_MEDIA_ASSET_SUCCESS:
            return produce(state, draft => {
                draft.mutatingAssetId = null;
                const asset = payload as IUserMediaAsset;
                const idx = draft.assets.findIndex(a => a.id === asset.id || a.url === asset.url);
                if (idx >= 0) draft.assets[idx] = { ...draft.assets[idx], ...asset };
            });
        case types.UPDATE_USER_MEDIA_ASSET_FAIL:
            return produce(state, draft => {
                draft.mutatingAssetId = null;
                draft.error = payload;
            });
        case types.DELETE_USER_MEDIA_ASSET:
            return produce(state, draft => {
                draft.mutatingAssetId = payload as string;
                draft.error = null;
            });
        case types.DELETE_USER_MEDIA_ASSET_SUCCESS:
            return produce(state, draft => {
                const id = payload as string;
                draft.mutatingAssetId = null;
                draft.assets = draft.assets.filter(a => a.id !== id);
                if (draft.pagination && draft.pagination.total > 0) {
                    draft.pagination.total -= 1;
                }
            });
        case types.DELETE_USER_MEDIA_ASSET_FAIL:
            return produce(state, draft => {
                draft.mutatingAssetId = null;
                draft.error = payload;
            });
        case types.CLEAR_USER_MEDIA_ASSETS:
            return produce(state, draft => {
                draft.assets = [];
                draft.pagination = null;
                draft.lastQuery = '';
                draft.listCacheAt = {};
                draft.error = null;
                draft.listSettled = false;
                draft.exhausted = false;
                draft.mutatingAssetId = null;
            });
        default:
            return state;
    }
};

export function isUserMediaListCacheFresh(
    listCacheAt: Record<string, number>,
    cacheKey: string,
    ttlMs = LIST_CACHE_TTL_MS,
): boolean {
    const at = listCacheAt[cacheKey];
    return typeof at === 'number' && Date.now() - at < ttlMs;
}
